import { upsertRoomStateCollaborationData } from '../../../firestore/RoomStateCollaborationData'
import { CollaborationDataType } from '../../../firestore/RoomStateCollaborationData/types'
import type {
  InteractiveFormElementSubController,
  SuggestionValue,
} from './InteractiveFormElementSubController'

export class SampleSuggestionGenerator {
  constructor(private subcontroller: InteractiveFormElementSubController) {}

  get controller() {
    return this.subcontroller.controller
  }

  get element() {
    return this.subcontroller.element
  }

  generateSampleSuggestions() {
    const suggestions: Record<
      string,
      SuggestionValue | Record<string, unknown> | unknown[]
    > = {}
    const schema = this.subcontroller.schema

    if (!schema.success) return

    const fieldNames = Array.from(Object.keys(schema.data.properties))

    for (const fieldName of fieldNames) {
      switch (fieldName) {
        case 'aim':
          suggestions[fieldName] =
            'Students will analyze the early development of Airbnb, evaluating how strategic decisions and prevailing market conditions influenced its growth trajectory, and assess the broader implications for startup success.'
          break
        case 'moduleType':
          suggestions[fieldName] = 'scenario'
          break
        case 'successMetrics':
          suggestions[fieldName] = ['knowledge']
          break
        case 'summary':
          suggestions[fieldName] =
            'This case study explores the strategic growth of Airbnb, highlighting key decisions and market dynamics that propelled the startup to success.'
          break
        case 'title':
          suggestions[fieldName] =
            'The Rise of Airbnb: A Case Study on Startup Growth and Market Disruption'
          break
        case 'subtitle':
          suggestions[fieldName] =
            'Understanding the Dynamics of a Successful Startup'
          break
        case 'keyConcepts':
          suggestions[fieldName] = [
            'Startup Growth',
            'Market Disruption',
            'Strategic Decision-Making',
            'Business Model Innovation',
          ]
          break
        case 'learningObjectives':
          suggestions[fieldName] = [
            'Analyze the impact of strategic decisions on startup growth',
            'Evaluate market conditions that influence startup success',
            'Assess the role of innovation in business model development',
          ]
          break
        case 'segments':
          suggestions[fieldName] = this.segmentsSuggestion()
          break
        case 'slideDeckJSON':
          suggestions[fieldName] = this.previewSuggestion()
          break
        default:
          break
      }
    }

    return upsertRoomStateCollaborationData(
      this.controller.meeting.repository,
      {
        id: `suggestions-${this.element.id}`,
        roomId: this.controller.meeting.roomId,
        data: {
          questionId: this.element.id,
          data: suggestions,
          collaborationDataType:
            CollaborationDataType.interactiveFormSuggestions,
        },
        merge: true,
      }
    )
  }

  segmentsSuggestion() {
    return [
      {
        id: '1',
        exhibits: [],
        slides: [
          {
            duration: 300,
            sequence: 1,
            summary:
              'Participants reflect individually on the early choices made by Airbnb’s founders and then share their selection and reasoning with the group, citing specific examples from the case. This primes the group for deeper analysis of strategic decisions that shaped Airbnb’s trajectory early on.\n\n*Key focus: Initial product focus, pricing, platform design, or funding strategies.*',
            title: 'Most Pivotal Early Decision?',
            type: 'Discussion',
          },
          {
            duration: 120,
            sequence: 2,
            summary:
              'A poll is introduced where participants select which early strategic decision they believe had the greatest impact on Airbnb’s initial growth trajectory. Choices are based on the major decisions discussed in the previous slide. This captures initial opinions and sets up the following discussion.\n\n*Poll options: Events-based rentals, Design-focused branding, Unconventional funding sources, Other (specify).*',
            title: 'Poll: Which Early Decision Had the Greatest Impact?',
            type: 'Poll',
          },
          {
            duration: 300,
            sequence: 3,
            summary:
              'Participants share their poll choices and explain their reasoning using a Pro/Con T-Chart to evaluate at least two major early decisions. The discussion focuses on the merits and drawbacks of each decision, encouraging evidence-based debate.\n\n*Guidance: Share your poll choice and use the T-Chart to articulate why you believe your selected decision was most critical. Listen for alternative perspectives and be ready to challenge or defend your view.*',
            title: 'Share & Defend: Which Decision Was Most Critical?',
            type: 'Discussion',
          },
          {
            duration: 240,
            sequence: 4,
            summary:
              'Participants individually consider which early strategic decision they would have prioritized as an Airbnb founder, then support their answer with two specific reasons. This encourages personal reflection and application of insights from the discussion.\n\n*Key focus: Personal prioritization and rationale.*',
            title: 'If You Were a Founder...',
            type: 'Discussion',
          },
        ],
        summary:
          'This segment explores the foundational choices that shaped Airbnb’s early trajectory. Participants analyze, debate, and defend the impact of key strategic decisions, culminating in personal reflection on founder priorities.',
        title: 'Strategic Decisions in Airbnb’s Early Growth',
      },
      {
        id: '2',
        exhibits: [
          {
            description:
              'Table summarizing the economic, technological, and competitive environment Airbnb faced at launch.',
            name: 'Exhibit 2-1: Early Market Conditions for Airbnb',
          },
        ],
        slides: [
          {
            duration: 300,
            sequence: 1,
            summary:
              'Participants discuss how external factors shaped Airbnb’s opportunities and challenges. They refer to Exhibit 2-1 to identify which market condition most strongly contributed to or hindered Airbnb’s early growth, supporting their points with evidence from the exhibit.\n\n*Key focus: Economic climate, consumer trust, tech adoption, competitive landscape.*',
            title:
              'How Did Market Conditions Shape Airbnb’s Opportunities and Challenges?',
            type: 'Discussion',
          },
          {
            duration: 120,
            sequence: 2,
            summary:
              'A poll is introduced where participants rank which market condition they believe was most important for Airbnb’s early success. This quickly surfaces initial group perspectives and prepares the group for deeper justification and debate.\n\n*Poll options: Economic Climate, Consumer Trust, Tech Adoption, Competitive Landscape.*',
            title: 'Poll: Most Important Market Condition?',
            type: 'Poll',
          },
          {
            duration: 300,
            sequence: 3,
            summary:
              'Participants share their poll rankings and justify their choices using the provided table. The discussion focuses on the rationale behind each ranking, with attention to disagreements and consensus-building.\n\n*Guidance: Share your poll choice, explain your rationale, and listen for alternative perspectives. Work toward consensus where possible.*',
            title: 'Share & Justify: Ranking Market Conditions',
            type: 'Discussion',
          },
          {
            duration: 240,
            sequence: 4,
            summary:
              'Participants consider if changing one market condition could have further accelerated Airbnb’s growth. They discuss which condition they would alter and hypothesize how this might have changed the company’s trajectory.\n\n*Key focus: Counterfactual thinking and impact assessment.*\n\n*Reference: Exhibit 2-1 as needed.*',
            title: 'If You Could Change One Market Condition...',
            type: 'Discussion',
          },
        ],
        summary:
          'This segment examines how market forces shaped Airbnb’s early journey. Participants analyze, rank, and debate the relative impact of different market conditions, culminating in a counterfactual exercise.',
        title: 'Market Conditions and Startup Growth',
      },
      {
        id: '3',
        exhibits: [],
        slides: [
          {
            duration: 300,
            sequence: 1,
            summary:
              'Participants individually consider whether Airbnb’s success was more dependent on its innovative business model or the timing of its market entry. They prepare to defend their stance with evidence from the reading, setting up a debate on the drivers of disruptive success.\n\n*Key focus: Innovation vs. market readiness.*',
            title: 'Innovation or Timing: What Drove Airbnb’s Disruption?',
            type: 'Discussion',
          },
          {
            duration: 120,
            sequence: 2,
            summary:
              'A poll is introduced where participants select whether they believe Airbnb’s disruptive model was more dependent on innovation or market readiness. This captures initial positions and frames the subsequent group discussion.\n\n*Poll options: Innovation, Market Readiness, Both Equally.*\n\n*Purpose: Surface the group’s distribution of opinion before deeper analysis.*',
            title: 'Poll: Innovation or Market Readiness?',
            type: 'Poll',
          },
          {
            duration: 300,
            sequence: 3,
            summary:
              'Participants share their poll choices and defend their reasoning. The group discusses evidence for each stance, referencing examples from Airbnb’s journey to support their arguments.\n\n*Guidance: Share your poll choice and cite specific evidence from the case. Listen for counterarguments and refine your position as needed.*',
            title: 'Share & Defend: What Drove Airbnb’s Success?',
            type: 'Discussion',
          },
          {
            duration: 300,
            sequence: 4,
            summary:
              'Participants brainstorm and categorize key factors that differentiate successful platform-based startups from those that fail. They then select and justify the top three most critical factors for success, focusing on internal vs. external drivers.\n\n*Key focus: Network effects, regulatory navigation, trust-building, timing, adaptability, etc.*',
            title: 'Key Success Factors for Platform Startups',
            type: 'Discussion',
          },
          {
            duration: 240,
            sequence: 5,
            summary:
              'Participants reflect on the most important lesson about disruptive innovation and market timing to share with a future founder, supporting their answer with examples from Airbnb’s journey. This consolidates insights from the segment into actionable advice.\n\n*Key focus: Synthesis and application.*\n\n*Reference: Prior discussion and case examples.*',
            title: 'Most Important Lesson for Future Founders',
            type: 'Discussion',
          },
        ],
        summary:
          'This segment challenges participants to debate the relative influence of innovation versus timing in Airbnb’s disruption. Through polls, discussion, and synthesis, participants extract lessons for future founders.',
        title: 'Disruption, Timing, and Startup Success',
      },
    ]
  }

  previewSuggestion() {
    return {
      slideDeckDescription:
        'This module guides students through a case-based discussion on Airbnb’s early development, focusing on how key strategic decisions and prevailing market conditions influenced its growth trajectory. By examining real-world scenarios, students will gain insights into the dynamics of entrepreneurship, innovation, and market adaptation.',
      slideDeckDisciplines: [],
      slideDeckExhibits: [
        {
          SlideId: 'slide-10',
          exhibitDescription: 'Market conditions 2008–2010',
          exhibitId: 'exhibit-1',
          exhibitMarkdown:
            '### Market Conditions Snapshot (2008–2010)\n\n- **Economic Climate:** Global recession led to increased financial pressure on consumers, making affordable alternatives to hotels more attractive.\n- **Competitor Landscape:** Few direct competitors in the peer-to-peer lodging space, but traditional hotels and Craigslist offered indirect competition.\n- **Regulatory Environment:** Uncertainty around legality of short-term rentals; evolving local regulations created both barriers and opportunities.\n- **Customer Sentiment:** Early adopters were cautious about trust and safety, but economic pressures and positive experiences drove gradual acceptance.',
          exhibitName: 'Exhibit 2-1',
        },
        {
          SlideId: 'slide-8',
          exhibitDescription: 'Early strategic decision',
          exhibitId: 'exhibit-2',
          exhibitMarkdown:
            '<div style="text-align: center; width:75%; margin: 0 auto;">\n\n# Poll Question\n\n\nWhich early strategic decision had the greatest impact on Airbnb’s ability to survive and grow?\n\n</div>\n\n\n<div style="display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; max-width: 900px; margin: 0 auto;">\n      <div style="text-align: center; background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #f6c000; margin-bottom: 10px;">A</div>\n        <div>Focus on conventions</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #8ce7f0; margin-bottom: 10px;">B</div>\n        <div>Cereal campaign</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #e05a28; margin-bottom: 10px;">C</div>\n        <div>Trust features</div>\n      </div>\n    </div>\n\n',
          exhibitName: 'Poll',
        },
        {
          SlideId: 'slide-13',
          exhibitDescription: 'Most influential market condition',
          exhibitId: 'exhibit-3',
          exhibitMarkdown:
            '<div style="text-align: center; width:75%; margin: 0 auto;">\n\n# Poll Question\n\n\nWhich market condition most strongly shaped Airbnb’s early trajectory?\n\n</div>\n\n\n<div style="display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; max-width: 900px; margin: 0 auto;">\n      <div style="text-align: center; background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #f6c000; margin-bottom: 10px;">A</div>\n        <div>Economic Climate</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #8ce7f0; margin-bottom: 10px;">B</div>\n        <div>Competitor Landscape</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #e05a28; margin-bottom: 10px;">C</div>\n        <div>Regulatory Environment</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #b69a27; margin-bottom: 10px;">D</div>\n        <div>Customer Sentiment</div>\n      </div>\n    </div>\n\n',
          exhibitName: 'Poll',
        },
        {
          SlideId: 'slide-18',
          exhibitDescription: 'Most critical stakeholder group',
          exhibitId: 'exhibit-4',
          exhibitMarkdown:
            '<div style="text-align: center; width:75%; margin: 0 auto;">\n\n# Poll Question\n\n\nWhich stakeholder group was most critical to Airbnb’s early success?\n\n</div>\n\n\n<div style="display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; max-width: 900px; margin: 0 auto;">\n      <div style="text-align: center; background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #f6c000; margin-bottom: 10px;">A</div>\n        <div>Guests</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #8ce7f0; margin-bottom: 10px;">B</div>\n        <div>Hosts</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #e05a28; margin-bottom: 10px;">C</div>\n        <div>Regulators</div>\n      </div>\n      <div style="text-align: center;background-color: #1f1f1f; color: white; border-radius: 16px; padding: 30px 20px; box-shadow: 0 8px 16px rgba(0,0,0,0.15); min-width: 22%; flex: 1 1 180px;">\n        <div style="font-size: 2em; font-weight: bold; color: #b69a27; margin-bottom: 10px;">D</div>\n        <div>Investors</div>\n      </div>\n    </div>\n\n',
          exhibitName: 'Poll',
        },
      ],
      slideDeckKeyConcepts: [
        'Airbnb’s founding and early growth',
        'Strategic decision-making in startups',
        'Market adaptation and innovation',
        'Entrepreneurship in platform businesses',
        'External market factors influencing growth',
      ],
      slideDeckLearningObjectives: [
        'Evaluate key strategic decisions made by Airbnb in its early stages.',
        'Analyze the impact of market conditions on Airbnb’s development.',
        'Understand the role of innovation and adaptation in entrepreneurial success.',
        'Assess how external factors influence startup growth trajectories.',
      ],
      slideDeckName:
        'Analyzing Airbnb’s Early Growth: Strategic Decisions and Market Forces',
      slideDeckPhraseSet: [
        'Airbnb',
        'platform business',
        'market adaptation',
        'startup growth',
        'strategic decision',
        'market conditions',
        'innovation dynamics',
        'entrepreneurship',
        'stakeholder group',
        'trust features',
        'regulatory environment',
        'economic climate',
        'customer sentiment',
        'competitor landscape',
        'peer-to-peer lodging',
      ],
      slideDeckPreQuizQuestions: [],
      slideDeckTeaser:
        "Evaluating Entrepreneurial Decisions and External Factors in Airbnb's Initial Expansion",
      slideDeckType: 1,
      slides: [
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: 'Wait here until your full group is ready',
          slideDuration: 0,
          slideGroupTalkTrack: '',
          slideHeading: 'Welcome',
          slideId: 'slide-1',
          slideImageAltText:
            "Analyzing Airbnb’s Early Growth: Strategic Decisions and Market Forces\nEvaluating Entrepreneurial Decisions and External Factors in Airbnb's Initial Expansion",
          slideMarkdown:
            '<div style="display: flex; gap: 4em; align-items: center;">\n\n<div style="flex: 1;">\n\n<img src="/assets/images/logo.png" alt="Breakout Learning" style="width: 3.75em;" />\n\n## Welcome to your Breakout Room.\n\nWhen you\'re ready, advance to the next slide to begin the experience.\n\n</div>\n\n<div style="padding:1em 1.5em; border-radius:0.75em; max-width:350px; box-shadow: 0 0.5em 1em rgba(0,0,0,0.15); flex-shrink: 0; text-align: center; display: flex; flex-direction: column; gap: 0.25em;">\n\nWe hope you enjoy\n\n\n### **Analyzing Airbnb’s Early Growth: Strategic Decisions and Market Forces** \n_Evaluating Entrepreneurial Decisions and External Factors in Airbnb\'s Initial Expansion_\n\n\nSincerely,\nThe Breakout Learning Team\n\n</div>\n \n</div>',
          slideName: 'Waiting Room',
          slideType: 18,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 0,
          slideGroupTalkTrack:
            'During this experience, you will: Evaluate Airbnb’s early strategic decisions, analyze market conditions, understand innovation’s role, and assess external factors on startup growth.',
          slideHeading: 'Read below and then advance to the next slide...',
          slideId: 'slide-2',
          slideImageAltText:
            '- Evaluate key strategic decisions made by Airbnb in its early stages.\n- Analyze the impact of market conditions on Airbnb’s development.\n- Understand the role of innovation and adaptation in entrepreneurial success.\n- Assess how external factors influence startup growth trajectories.',
          slideMarkdown:
            '# Learning Objectives\n\nDuring this experience, through collaboration and discussion with your group, you will:\n\n- Evaluate key strategic decisions made by Airbnb in its early stages.\n- Analyze the impact of market conditions on Airbnb’s development.\n- Understand the role of innovation and adaptation in entrepreneurial success.\n- Assess how external factors influence startup growth trajectories.',
          slideName: 'Learning Objectives',
          slideType: 18,
        },
        {
          allowSkipVideo: true,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: 'Global Intro Video - No Grading',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 0,
          slideGroupTalkTrack: '',
          slideHeading: 'Watch to learn what to expect...',
          slideId: 'slide-3',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'How it works',
          slideType: 0,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'How your instructor will evaluation this experience',
          slideDuration: 0,
          slideGroupTalkTrack:
            "Here's how your session is being scored. Remember that only your professor determines your final grade. Advance when you are ready.",
          slideHeading: 'Read below and then advance to the next slide...',
          slideId: 'slide-4',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Grading',
          slideType: 11,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [
              {
                cardDescription:
                  'Explore Airbnb’s founding choices and debate the impact of early decisions.',
                cardTitle: 'Founding Choices',
              },
              {
                cardDescription:
                  'Analyze how market forces shaped Airbnb’s growth and adaptation.',
                cardTitle: 'Market Forces',
              },
              {
                cardDescription:
                  'Assess Airbnb’s innovations and how they addressed external pressures.',
                cardTitle: 'Innovation & Adaptation',
              },
            ],
            contentDescription:
              'This experience is organized into three segments: founding choices, market forces, and innovation/adaptation. Each segment builds on the last to deepen your understanding of Airbnb',
            contentFooter:
              'Be prepared to discuss, debate, and defend your analysis throughout the session.',
            contentTitle: 'Agenda',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 0,
          slideGroupTalkTrack:
            'In this experience, you’ll engage in a series of discussion with your group. Review and then advance when you’re ready...',
          slideHeading: 'Read below and then advance to the next slide...',
          slideId: 'slide-5',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Agenda',
          slideType: 17,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to provide a specific decision and rationale, referencing the reading for support.',
              ask: 'What was the most pivotal early decision by Airbnb’s founders and why?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants individually identify what they believe was the most pivotal early decision by Airbnb’s founders, write down their answer and rationale, and then share with the group, referencing the reading for support.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Reflect and share what you think was Airbnb’s most pivotal early decision, and explain your reasoning.',
          slideHeading: 'Pivotal Early Decision',
          slideId: 'slide-6',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nIndividually identify the most pivotal early decision by Airbnb’s founders. Write down your answer and rationale, referencing the reading for support. Share your answer with the group.',
          slideName: 'Segment 1: Pivotal Early Decision',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [
            'Focus on conventions',
            'Cereal campaign',
            'Trust features',
          ],
          pollQuestion:
            'Which early strategic decision had the greatest impact on Airbnb’s ability to survive and grow?',
          slideDescription:
            'A poll is conducted where participants choose which early strategic decision (focus on conventions, cereal campaign, or trust features) they believe had the greatest impact on Airbnb’s ability to survive and grow.',
          slideDuration: 60,
          slideGroupTalkTrack:
            'Select the option you most agree with in the poll below. Be prepared to discuss your choice with the group.',
          slideHeading: 'Time for a poll...',
          slideId: 'slide-7',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Segment 1: Poll',
          slideType: 4,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to state their poll choice and defend their reasoning, referencing the Pro/Con T-Chart.',
              ask: 'Which option did you choose in the poll and why?',
            },
            {
              additionalInstructions:
                'Require group to discuss the primary benefits and drawbacks of each decision using the grid.',
              ask: 'What are the main benefits and drawbacks of each early decision?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants share which option they chose in the poll and defend their reasoning, referencing the Pro/Con T-Chart. The group discusses the primary benefits and drawbacks of each decision, using the grid to guide analysis and surface the rationale behind different choices.',
          slideDuration: 420,
          slideGroupTalkTrack:
            'Share your poll choice and defend your reasoning. Then discuss the main pros and cons of each decision.',
          slideHeading: 'Defend Your Choice',
          slideId: 'slide-8',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nShare which option you chose in the poll and defend your reasoning, referencing the Pro/Con T-Chart. As a group, discuss the primary benefits and drawbacks of each decision.',
          slideName: 'Segment 1: Defend Your Choice',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to provide two reasons for their choice and share briefly with the group.',
              ask: 'If you were in the founders’ shoes, which early decision would you have prioritized and why?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants individually reflect on which early decision they would have prioritized if they were in the founders’ shoes, supporting their answer with two specific reasons. Participants share briefly with the group.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Reflect on which decision you would have prioritized and share two reasons for your choice.',
          slideHeading: 'Your Priorities',
          slideId: 'slide-9',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nIndividually reflect on which early decision you would have prioritized if you were in the founders’ shoes. Support your answer with two specific reasons. Share briefly with the group.',
          slideName: 'Segment 1: Your Priorities',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to identify at least one challenge and one opportunity, citing evidence from Exhibit 2-1.',
              ask: 'How did external market conditions in 2008–2010 create challenges and opportunities for Airbnb?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants discuss how the external market conditions in 2008–2010 created both challenges and opportunities for Airbnb. They are instructed to refer to Exhibit 2-1, identifying at least one challenge and one opportunity, citing evidence from the exhibit.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Discuss how the 2008–2010 market conditions created challenges and opportunities for Airbnb. Use Exhibit 2-1 for evidence.',
          slideHeading: 'Market Challenges & Opportunities',
          slideId: 'slide-10',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nDiscuss how the external market conditions in 2008–2010 created both challenges and opportunities for Airbnb. Refer to Exhibit 2-1. Identify at least one challenge and one opportunity, citing evidence from the exhibit.',
          slideName: 'Segment 2: Market Challenges & Opportunities',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [
            'Economic Climate',
            'Competitor Landscape',
            'Regulatory Environment',
            'Customer Sentiment',
          ],
          pollQuestion:
            'Which market condition from Exhibit 2-1 most strongly shaped Airbnb’s early trajectory?',
          slideDescription:
            'A poll asks participants to select which single market condition from Exhibit 2-1 they believe most strongly shaped Airbnb’s early trajectory. Participants choose one of four factors: Economic Climate, Competitor Landscape, Regulatory Environment, or Customer Sentiment.',
          slideDuration: 60,
          slideGroupTalkTrack:
            'Choose the market condition you believe most influenced Airbnb’s early trajectory. Be ready to discuss your reasoning.',
          slideHeading: 'Time for a poll...',
          slideId: 'slide-11',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Segment 2: Poll',
          slideType: 4,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to state their poll choice and explain their reasoning.',
              ask: 'Which market condition did you choose and why?',
            },
            {
              additionalInstructions:
                'Require group to complete a Priority Ranking of the four factors and discuss rationale.',
              ask: 'Rank the four market conditions from most to least influential and explain your group’s rationale.',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants share which market condition they chose in the poll and explain their reasoning. The group completes a Priority Ranking, ordering the four factors from most to least influential, and discusses the rationale for their rankings, using evidence from Exhibit 2-1.',
          slideDuration: 420,
          slideGroupTalkTrack:
            'Share your poll choice and reasoning, then work together to rank the four market conditions by influence.',
          slideHeading: 'Defend & Rank',
          slideId: 'slide-12',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nShare which market condition you chose in the poll and explain your reasoning. As a group, complete a Priority Ranking of the four factors from most to least influential, using evidence from Exhibit 2-1.',
          slideName: 'Segment 2: Defend & Rank',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to debate how Airbnb’s path might have changed if one market factor was significantly different, supporting arguments with evidence from Exhibit 2-1.',
              ask: 'How might Airbnb’s trajectory have changed if one market condition was significantly different?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants debate how Airbnb’s path might have changed if one of the market factors had been significantly different (e.g., a strong economy instead of a recession), supporting their arguments with evidence from Exhibit 2-1 and prior discussion.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Debate how Airbnb’s path might have changed if one market factor was different. Use evidence from Exhibit 2-1.',
          slideHeading: 'Counterfactual Scenario',
          slideId: 'slide-13',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nDebate how Airbnb’s path might have changed if one of the market factors had been significantly different. Support your arguments with evidence from Exhibit 2-1 and prior discussion.',
          slideName: 'Segment 2: Counterfactual Scenario',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to identify the most innovative adaptation and explain why it stands out.',
              ask: 'What was the most innovative adaptation Airbnb made in response to external pressures and why?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants individually identify the most innovative adaptation Airbnb made in response to external pressures during its early years and explain why it stands out. They then share their examples and reasoning with the group.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Identify Airbnb’s most innovative adaptation to external pressures and explain why it stands out.',
          slideHeading: 'Most Innovative Adaptation',
          slideId: 'slide-14',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nIndividually identify the most innovative adaptation Airbnb made in response to external pressures during its early years and explain why it stands out. Share your example and reasoning with the group.',
          slideName: 'Segment 3: Most Innovative Adaptation',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: ['Guests', 'Hosts', 'Regulators', 'Investors'],
          pollQuestion:
            'Which stakeholder group was most critical to Airbnb’s early success?',
          slideDescription:
            'A poll asks participants to choose which stakeholder group (guests, hosts, regulators, or investors) they believe was most critical to Airbnb’s early success.',
          slideDuration: 60,
          slideGroupTalkTrack:
            'Select the stakeholder group you believe was most critical to Airbnb’s early success. Be ready to discuss.',
          slideHeading: 'Time for a poll...',
          slideId: 'slide-15',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Segment 3: Poll',
          slideType: 4,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to state their poll choice and defend their reasoning, referencing the Stakeholder Matrix.',
              ask: 'Which stakeholder group did you choose in the poll and why?',
            },
            {
              additionalInstructions:
                'Require group to analyze how Airbnb’s innovations affected each group’s willingness to support or oppose Airbnb.',
              ask: 'How did Airbnb’s innovations impact each stakeholder group’s willingness to support or oppose Airbnb?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants share which stakeholder group they chose in the poll and defend their reasoning, referencing the Stakeholder Matrix. The group analyzes how Airbnb’s innovations (e.g., trust/safety features, insurance guarantees) affected each group’s willingness to support or oppose Airbnb, focusing on the net effect for the group they selected.',
          slideDuration: 420,
          slideGroupTalkTrack:
            'Share your poll choice and defend your reasoning. Then discuss how Airbnb’s innovations affected each stakeholder group.',
          slideHeading: 'Defend Stakeholder Choice',
          slideId: 'slide-16',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nShare which stakeholder group you chose in the poll and defend your reasoning, referencing the Stakeholder Matrix. As a group, analyze how Airbnb’s innovations (e.g., trust/safety features, insurance guarantees) affected each group’s willingness to support or oppose Airbnb.',
          slideName: 'Segment 3: Defend Stakeholder Choice',
          slideType: 2,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [
            {
              additionalInstructions:
                'Require participant to discuss how Airbnb’s approach to innovation helped overcome skepticism and regulatory uncertainty, using the Stakeholder Matrix.',
              ask: 'How did Airbnb’s innovations help overcome skepticism and regulatory uncertainty among stakeholders?',
            },
          ],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription:
            'Participants discuss how Airbnb’s approach to innovation helped it overcome skepticism and regulatory uncertainty, using the Stakeholder Matrix to analyze the effects of a major adaptation on different groups.',
          slideDuration: 300,
          slideGroupTalkTrack:
            'Discuss how Airbnb’s innovations helped overcome skepticism and regulatory uncertainty among stakeholders.',
          slideHeading: 'Overcoming Skepticism',
          slideId: 'slide-17',
          slideImageAltText: null,
          slideMarkdown:
            '# Discuss\n\nDiscuss how Airbnb’s approach to innovation helped it overcome skepticism and regulatory uncertainty, using the Stakeholder Matrix to analyze the effects of a major adaptation on different groups.',
          slideName: 'Segment 3: Overcoming Skepticism',
          slideType: 2,
        },
        {
          allowSkipVideo: true,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: 'Professor Feedback Video',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 0,
          slideGroupTalkTrack: '',
          slideHeading: 'Watch and Listen',
          slideId: 'slide-18',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Professor Feedback Video',
          slideType: 0,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 300,
          slideGroupTalkTrack:
            "It's your time to share any other thoughts you may have had about the material. You might talk about what stood out, what you want to learn more about, or any questions and points of confusion you still have.",
          slideHeading: 'Share your thoughts with your professor',
          slideId: 'slide-19',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Professor Feedback Discussion',
          slideType: 6,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 180,
          slideGroupTalkTrack: '',
          slideHeading: 'Thank you for using Breakout Learning',
          slideId: 'slide-20',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'Session Processing',
          slideType: 7,
        },
        {
          allowSkipVideo: false,
          animatedCards: {
            cards: [],
            contentDescription: '',
            contentFooter: '',
            contentTitle: '',
          },
          globalAssetName: '',
          moderationTasks: [],
          pollAnswers: [],
          pollQuestion: '',
          slideDescription: null,
          slideDuration: 0,
          slideGroupTalkTrack: '',
          slideHeading: 'Session Results',
          slideId: 'slide-21',
          slideImageAltText: null,
          slideMarkdown: null,
          slideName: 'AI-generated Results',
          slideType: 8,
        },
      ],
    }
  }
}
